<template>
  <v-container id="admin" fluid>
    <v-card>
      <v-progress-linear
        :active="processingUsers"
        :indeterminate="processingUsers"
        color="red"
      />
      <v-system-bar color="user" />
      <v-dialog v-model="deleteDialog" @keydown.esc="deleteDialog = false">
        <confirm-delete-card @cancel="deleteDialog = false" />
      </v-dialog>
      <v-dialog
        v-model="snapshotDialog"
        @keydown.esc="snapshotDialog = false"
        max-width="720px"
      >
        <device-snapshot-card
          :device="selectedDevice"
          @cancel="snapshotDialog = false"
          transition="slide-x-reverse-transition"
        />
      </v-dialog>
      <v-toolbar flat>
        <v-icon large left color="user">mdi-account</v-icon>
        <v-toolbar-title>ユーザー</v-toolbar-title>
        <v-spacer />
        <v-text-field
          v-model="userSearch"
          prepend-icon="mdi-magnify"
          label="検索"
          single-line
          hide-details
          outlined
          dense
          clearable
        />
        <v-spacer />
        <v-btn color="user" @click="setAdminRole(true)" :disabled="!anyUsers">
          Make Admin
        </v-btn>
        <v-btn color="user" @click="setAdminRole(false)" :disabled="!anyUsers">
          Remove Admin
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-data-table
          v-model="selectedUsers"
          :items="users"
          :headers="userHeaders"
          :loading="loadingUsers"
          :search="userSearch"
          dense
          show-select
        >
          <template v-slot:[`item.owned`]="{ item }">
            <v-chip label v-if="getDevicesOwnedByUser(item).length === 0">
              None
            </v-chip>
            <v-chip
              v-else
              label
              close
              class="ma-1"
              color="device"
              v-for="device in getDevicesOwnedByUser(item)"
              :key="device.id"
              @click="focusDeviceId(device.id)"
              @click:close="assignOwner([device], null)"
            >
              {{ device.name }}
            </v-chip>
          </template>
          <template v-slot:[`item.admin`]="{ item }">
            <v-switch v-model="item.admin" flat disabled />
          </template>
          <template v-slot:[`item.signin`]="{ item }">
            <v-btn
              color="user"
              @click="signInAs(item.id)"
              :disabled="isCurrentUser(item.id)"
              >ログイン</v-btn
            >
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-card>
      <v-system-bar color="device" />
      <v-toolbar flat>
        <v-icon left large color="device">mdi-camera-control</v-icon>
        <v-toolbar-title>デバイス</v-toolbar-title>
        <v-spacer />
        <v-text-field
          v-model="deviceSearch"
          prepend-icon="mdi-magnify"
          label="検索"
          single-line
          hide-details
          clearable
          outlined
          dense
        />
        <v-spacer />
        <v-menu
          v-model="deviceRegisterMenu"
          offset-y
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on }">
            <v-btn color="success" v-on="on">
              登録
            </v-btn>
          </template>
          <v-card class="ma-0">
            <v-card-text>
              <v-text-field
                v-model="deviceRegisterId"
                label="Cloud ID"
                single-line
                dense
                autofocus
                clearable
              />
              <v-autocomplete
                v-model="deviceRegisterOwnerId"
                :items="users"
                item-text="email"
                item-value="id"
                label="Owner"
                clearable
              >
              </v-autocomplete>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="deviceRegisterMenu = false">Cancel</v-btn>
              <v-btn
                text
                color="device"
                :loading="loadingDevices"
                @click="
                  registerDeviceId(deviceRegisterId, deviceRegisterOwnerId)
                "
                >Register</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-menu>
        <v-edit-dialog
          large
          @save="assignOwner(selectedDevices, selectedOwner)"
        >
          <v-btn color="device" :disabled="!anyDevices">
            Assign
          </v-btn>
          <template v-slot:input>
            <v-autocomplete
              v-model="selectedOwner"
              :items="users"
              item-text="email"
              item-value="id"
              hint="Search User"
              autofocus
              clearable
              auto-select-first
            >
            </v-autocomplete>
          </template>
        </v-edit-dialog>
        <v-btn
          color="device"
          @click="assignOwner(selectedDevices, null)"
          :disabled="!anyDevices"
        >
          Release
        </v-btn>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              @click="snapshotDialog = true"
              :disabled="!oneDeviceSelected"
              color="device"
            >
              Snapshot
            </v-btn>
          </template>
          <span>Camera snapshot</span>
        </v-tooltip>
      </v-toolbar>
      <v-card-text>
        <v-data-table
          v-model="selectedDevices"
          :items="devices"
          :headers="deviceHeaders"
          :loading="loadingDevices"
          :search="deviceSearch"
          dense
          show-select
        >
          <template v-slot:[`item.name`]="{ item }">
            <v-edit-dialog
              large
              :return-value.sync="item.name"
              @save="saveDevice(item)"
            >
              <v-btn small icon left><v-icon>mdi-playlist-edit</v-icon></v-btn>
              <span>{{ item.name }}</span>
              <template v-slot:input>
                <v-text-field
                  :rules="[v => v.length >= 3 || '3文字以上']"
                  v-model="item.name"
                  single-line
                  autofocus
                />
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:[`item.owner`]="{ item }">
            <v-chip
              v-if="item.owner"
              @click.stop="focusUserId(item.owner)"
              @click:close="assignOwner([item], null)"
              color="user"
              label
              close
            >
              {{ userIdToName(item.owner) }}
            </v-chip>
            <v-chip v-else label>
              Unassigned
            </v-chip>
          </template>
          <template v-slot:[`item.active`]="{ item }">
            <v-switch flat v-model="item.active" @change="saveDevice(item)" />
          </template>
          <template v-slot:[`item.updated`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ getDeviceOnlineUpdated(item) }}
                </span>
              </template>
              <span>{{ getDeviceOnlineUpdatedFull(item) }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.online`]="{ item }">
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-icon
                  large
                  v-on="on"
                  :color="item.online ? 'success' : 'error'"
                  >mdi-power</v-icon
                >
              </template>
              <span>
                {{ item.online ? 'オンライン' : 'オフライン' }}
              </span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- <div>
      <div>
        <v-btn v-on:click="deleteData">Delete Reports</v-btn>
      </div>
    </div> -->
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { functions } from '@/config/firebase'
import { fmt, fmtToNow } from '@/plugins/datefns'
import ConfirmDeleteCard from '@/components/dialogs/ConfirmDeleteCard'
import DeviceSnapshotCard from '@/components/device/DeviceSnapshotCard'
import { updateDevice } from '@/firebaseAccess/firestoreAccess'

export default {
  name: 'DashboardAdmin',
  components: { ConfirmDeleteCard, DeviceSnapshotCard },
  data: () => ({
    deleteDialog: false,
    snapshotDialog: false,

    processingUsers: false,
    loadingUsers: false,
    userSearch: '',
    selectedUsers: [],
    userHeaders: [
      { text: 'Eメール', value: 'email' },
      { text: 'デバイス', value: 'owned', align: 'center', width: 300 },
      { text: 'UID', value: 'id', align: 'end' },
      { text: 'アドミン', value: 'admin', width: 110 },
      { text: '切替', value: 'signin' }
    ],

    loadingDevices: false,
    deviceSearch: '',
    selectedDevices: [],
    selectedOwner: null,
    deviceRegisterId: '',
    deviceRegisterOwnerId: '',
    deviceRegisterMenu: false,
    deviceHeaders: [
      { text: '名前', value: 'name' },
      { text: 'ユーザー', value: 'owner', align: 'center', width: 300 },
      { text: 'UID', value: 'id', align: 'end' },
      { text: 'Active', value: 'active', width: 110 },
      { text: '最終更新', value: 'updated', width: 140 },
      { text: 'ステータス', value: 'online', align: 'center', width: 120 }
    ]
  }),
  computed: {
    ...mapGetters({
      userId: 'users/getActiveUserId',
      users: 'users/getUsers',
      devices: 'devices/getDevices'
    }),
    selectedDevice() {
      if (this.oneDeviceSelected) {
        return this.selectedDevices[0]
      }
      return {}
    },
    oneDeviceSelected() {
      return this.selectedDevices.length === 1
    },
    anyDevices() {
      return this.selectedDevices.length > 0
    },
    anyUsers() {
      return this.selectedUsers.length > 0
    }
  },
  methods: {
    ...mapActions({
      showSnack: 'snackbar/showSnack',

      bindUsersRef: 'users/bindRef',
      bindDevicesRef: 'devices/bindAllRef',
      signInWithToken: 'users/signInWithToken',
      setToken: 'tokens/setToken'
    }),
    getDevicesOwnedByUser(user) {
      return this.devices.filter(d => d.owner === user.id)
    },
    getDeviceOnlineUpdatedFull(device) {
      if (device.updated !== undefined) {
        return fmt(device.updated.toDate())
      }
      return '--'
    },
    getDeviceOnlineUpdated(device) {
      if (device.updated !== undefined) {
        return fmtToNow(device.updated.toDate())
      }
      return '--'
    },
    setAdminRole(role) {
      // skip updating users that already have correct role
      const emails = this.selectedUsers
        .filter(u => u.admin !== role)
        .map(u => u.email)

      if (emails.length > 0) {
        this.loadingUsers = true
        const task = functions.httpsCallable('users-SetAdminRole')
        task({ emails: emails, role: role })
          .then(result => {
            if (result.data.success) {
              this.showSnack({ message: result.data.message })
            } else {
              this.showSnack({ message: result.data.message, color: 'error' })
            }
          })
          .catch(error => {
            this.showSnack({ message: error, color: 'error' })
          })
          .finally(() => {
            this.loadingUsers = false
          })
      }
    },
    userIdToName(id) {
      if (id && this.users.length > 0) {
        return this.users.find(u => u.id === id).email
      }
    },
    focusDeviceId(id) {
      this.deviceSearch = id
    },
    focusUserId(id) {
      if (id) {
        this.userSearch = this.users.find(u => u.id === id).email
      }
    },
    assignOwner(devices, owner) {
      if (devices.length > 0) {
        const tasks = []
        for (const device of devices) {
          device.owner = owner
          tasks.push(updateDevice(device.id, device))
        }
        Promise.all(tasks).then(() => {
          this.showSnack({ message: `Owner set to ${owner}` })
        })
      }
    },
    async saveDevice(device) {
      try {
        await updateDevice(device.id, device)
        this.showSnack({ message: 'Saved' })
      } catch (error) {
        this.showSnack({
          message: `保存に失敗しました: ${error}`,
          color: 'error'
        })
      }
    },
    registerDeviceId(id, owner) {
      if (!id) return

      this.loadingDevices = true
      const register = functions.httpsCallable('device-Register')
      register({
        deviceId: id,
        ownerId: owner
      })
        .then(result => {
          if (result.data.success) {
            this.showSnack({ message: result.data.message })
          } else {
            this.showSnack({ message: result.data.message, color: 'error' })
          }
        })
        .catch(error => {
          this.showSnack({ message: error, color: 'error' })
        })
        .finally(() => {
          this.loadingDevices = false
          this.deviceRegisterId = ''
          this.deviceRegisterOwnerId = ''
        })
    },
    isCurrentUser(userId) {
      if (!userId) {
        return true
      }

      return this.userId === userId
    },
    async signInAs(userId) {
      this.processingUsers = true

      const showError = () => {
        this.showSnack({
          message: 'ユーザーの切り替えに失敗しました',
          color: 'error'
        })
      }

      // Get admin user's auth token.
      const adminToken = await this.getUserToken(this.userId)
      if (!adminToken) {
        this.processingUsers = false
        showError()
        return
      }

      // Get other users auth token.
      const userToken = await this.getUserToken(userId)
      if (!userToken) {
        this.processingUsers = false
        showError()
        return
      }

      try {
        // Sign in as a given user.
        const signInResult = await this.signInWithToken({
          token: userToken
        })

        if (signInResult.uid === userId) {
          // Save admin user's auth token in store.
          this.setToken({ token: adminToken })

          this.$router.push('/client/report')

          this.showSnack({ message: 'ユーザーの切り替えに成功しました' })
        } else {
          showError()
        }
      } catch (error) {
        showError()
      }

      this.processingUsers = false
    },
    async getUserToken(userId) {
      const publishUserToken = functions.httpsCallable(
        'users-PublishCustomToken'
      )
      let userToken = null
      try {
        const result = await publishUserToken({
          userId: userId
        })

        if (result.data.success) {
          userToken = result.data.data
        } else {
          console.log(result.data.message)
        }
      } catch (error) {
        console.log(error)
      }

      return userToken
    }
    /** Leave delete device events and reports for future reference */
    // deleteData() {
    //   // const deviceId = 'QyQVvbQmjXIncbJ40ymC'
    //   // const deviceId = 'TjBawbASSWUjfYXEAf5U'
    //   const deviceId = 'hBQiHcMu7zUYxFWx7SEv'
    //   // 2021/01/01
    //   const startDate = new Date(2021, 0, 1, 0, 0, 0)
    //   // 2021/06/01
    //   const endDate = new Date(2021, 5, 1, 23, 59, 59)

    //   this.deleteEvents(deviceId, startDate, endDate)
    //   this.deleteReports(deviceId, startDate, endDate)
    // },
    // async deleteEvents(deviceId, startDate, endDate) {
    //   // Collect session data between start date and end date.
    //   const eventsRef = await db
    //     .collection('devices')
    //     .doc(deviceId)
    //     .collection('events')
    //     .orderBy('startTimestamp', 'asc')
    //     .startAt(startDate)
    //     .endAt(endDate)
    //     .get()

    //   const deleteTasks = []
    //   for (const doc of eventsRef.docs) {
    //     const deleteTask = db
    //       .collection('devices')
    //       .doc(deviceId)
    //       .collection('events')
    //       .doc(doc.id)
    //       .delete()
    //     deleteTasks.push(deleteTask)
    //   }

    //   Promise.all(deleteTasks)
    //     .then(res => {
    //       console.log('delete events: success')
    //       console.log(res)
    //     })
    //     .catch(error => {
    //       console.log('delete events: fail')
    //       console.log(error)
    //     })
    // },
    // async deleteReports(deviceId, startDate, endDate) {
    //   const startDateSeconds = Math.floor(startDate.getTime() / 1000)
    //   const endDateSeconds = Math.floor(endDate.getTime() / 1000)

    //   const genderReportsRef = await db
    //     .collection('reports')
    //     .doc(deviceId)
    //     .collection('details')
    //     .where('info.type', '==', Pick.Report.ReportInfo.Type.TYPE_GENDER)
    //     .orderBy('info.timestamp.seconds', 'asc')
    //     .startAt(startDateSeconds)
    //     .endAt(endDateSeconds)
    //     .get()

    //   const detectionReportsRef = await db
    //     .collection('reports')
    //     .doc(deviceId)
    //     .collection('details')
    //     .where('info.type', '==', Pick.Report.ReportInfo.Type.TYPE_DETECTION)
    //     .orderBy('info.timestamp.seconds', 'asc')
    //     .startAt(startDateSeconds)
    //     .endAt(endDateSeconds)
    //     .get()

    //   const deleteTasks = []
    //   for (const doc of genderReportsRef.docs) {
    //     const deleteTask = db
    //       .collection('reports')
    //       .doc(deviceId)
    //       .collection('details')
    //       .doc(doc.id)
    //       .delete()
    //     deleteTasks.push(deleteTask)
    //   }
    //   for (const doc of detectionReportsRef.docs) {
    //     const deleteTask = db
    //       .collection('reports')
    //       .doc(deviceId)
    //       .collection('details')
    //       .doc(doc.id)
    //       .delete()
    //     deleteTasks.push(deleteTask)
    //   }

    //   Promise.all(deleteTasks)
    //     .then(res => {
    //       console.log('delete report: success')
    //       console.log(res)
    //     })
    //     .catch(error => {
    //       console.log('delete report: error')
    //       console.log(error)
    //     })
    // }
  },
  created() {
    const users = this.bindUsersRef()
    const devices = this.bindDevicesRef()
    Promise.all([users, devices]).then(() => {
      this.loadingUsers = false
      this.loadingDevices = false
    })
  }
}
</script>

<style scoped></style>
